/*
 * STRING wizard input component
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Renderer2,
  ViewChild
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

@Component({
    selector: 'reporter-string',
    templateUrl: './string.component.html',
    styleUrls: ['./string.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => StringComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => StringComponent),
            multi: true
        },
    ],
    standalone: false
})

export class StringComponent implements ControlValueAccessor, Validator, AfterViewInit {

  constructor(private renderer: Renderer2, private elementRef: ElementRef) {
  }

  @ViewChild('field', { static: true }) field: ElementRef;

  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChange(v);
    }
  }

  get value() {
    return this.innerValue;
  }

  get required(): boolean {
    return this.elementRef.nativeElement.attributes.hasOwnProperty('required');
  }

  get maxLength() {
    return this.elementRef.nativeElement.attributes.maxlength ? this.elementRef.nativeElement.attributes.maxlength.value : undefined;
  }

  innerValue: any;
  initialValue: string;
  initialized = false;

  onChange = (_: any) => {};
  onTouched = () => {};

  ngAfterViewInit() {
    this.initialValue = this.value;

    if (this.required) {
      this.renderer.setAttribute(this.field.nativeElement, 'required', '');
    }

    if (this.elementRef.nativeElement.classList.contains('required')) {
      this.renderer.addClass(this.field.nativeElement, 'required');
    }
    this.initialized = true;
  }

  writeValue(value: any): void {
    this.innerValue = value;
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', value);
    this.onChange(value);
    this.onTouched();
  }

  registerOnChange(fn: (value: any) => any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  validate(control: FormControl): ValidationErrors {
    if (!this.initialized) {
      return null;
    }

    if (this.required && !control.value) {
      return {
        required: true,
        message: ' is required.'
      };
    }

    if (this.initialValue !== control.value && this.maxLength && control.value && control.value.length > this.maxLength) {
      return {
        maxlength: true,
        message: ` must not exceed ${ this.maxLength } characters.`
      };
    }
    return null;
  }
}
